export const RAG_API_BASE_URL =
  'https://praedictoprojectsd8o9pebg-rag-api-ecv.functions.fnc.fr-par.scw.cloud';

export const S3_PUBLIC_BASE =
  'https://iec-cp-public.s3.eu-central-1.amazonaws.com';

export const WP_BASE_URL = 'https://cleanroom-processes.de';

export const NAMESPACE_OPTIONS = [
  { value: 'public', label: 'Public (Companies, News, Products)' },
  { value: 'premium', label: 'Premium (Lectures, Files)' },
  { value: 'federation', label: 'Federation (All)' },
];

export const LOCALSTORAGE_KEYS = {
  public: 'rag_chatbot_key_public',
  premium: 'rag_chatbot_key_premium',
  federation: 'rag_chatbot_key_federation',
  selectedNamespace: 'rag_chatbot_selected_namespace',
  maxSources: 'rag_chatbot_max_sources',
};

// --- Entity type configuration ---

const ENTITY_CONFIG = {
  company: {
    label: 'Unternehmen',
    color: 'primary',
    icon: 'building',
    imageUrl: (fileName) =>
      fileName ? `${S3_PUBLIC_BASE}/companyImages/${fileName}` : null,
    adminPath: (entity) => `/admin/company/${entity.id}`,
    getDisplayName: (entity, lang) => entity.name || '',
    getImage: (entity) => entity.image?.main?.fileName,
    getCompanyName: () => null,
    query: /* GraphQL */ `
      query GetCompany($id: ID!) {
        getCompany(id: $id) {
          id
          name
          image { main { fileName alt } }
          website
        }
      }
    `,
    queryName: 'getCompany',
  },
  news: {
    label: 'News',
    color: 'info',
    icon: 'newspaper',
    imageUrl: (fileName) =>
      fileName ? `${S3_PUBLIC_BASE}/newsImages/${fileName}` : null,
    adminPath: (entity) =>
      entity.company?.id
        ? `/admin/news/${entity.company.id}/${entity.id}`
        : null,
    getDisplayName: (entity, lang) => {
      const entry = entity.name?.find((n) => n.language === lang);
      return entry?.text || entity.name?.[0]?.text || '';
    },
    getImage: (entity) => entity.image?.fileName,
    getCompanyName: (entity) => entity.company?.name || '',
    query: /* GraphQL */ `
      query GetCMSNews($id: ID!) {
        getCMSNews(id: $id) {
          id
          name { language text }
          image { fileName }
          company { id name }
        }
      }
    `,
    queryName: 'getCMSNews',
  },
  product: {
    label: 'Produkt',
    color: 'success',
    icon: 'box',
    imageUrl: (fileName) =>
      fileName ? `${S3_PUBLIC_BASE}/ProductImages/${fileName}` : null,
    adminPath: (entity) => `/admin/product/${entity.id}`,
    getDisplayName: (entity, lang) => {
      const entry = entity.name?.find((n) => n.language === lang);
      return entry?.text || entity.name?.[0]?.text || '';
    },
    getImage: (entity) =>
      entity.previewImage?.fileName || entity.images?.main?.fileName,
    getCompanyName: (entity) => entity.company?.name || '',
    query: /* GraphQL */ `
      query GetCMSProduct($id: ID!) {
        getCMSProduct(id: $id) {
          id
          name { language text }
          previewImage { fileName }
          images { main { fileName } }
          company { id name }
        }
      }
    `,
    queryName: 'getCMSProduct',
  },
  lecture: {
    label: 'Vortrag',
    color: 'warning',
    icon: 'chalkboard-teacher',
    imageUrl: () => null,
    adminPath: (entity) =>
      entity.company?.id
        ? `/admin/lecture/${entity.company.id}/${entity.id}`
        : null,
    getDisplayName: (entity, lang) => {
      const entry = entity.title?.find((n) => n.language === lang);
      return entry?.text || entity.title?.[0]?.text || '';
    },
    getImage: () => null,
    getCompanyName: (entity) => entity.company?.name || '',
    query: /* GraphQL */ `
      query GetLecture($id: ID!) {
        getLecture(id: $id) {
          id
          title { language text }
          company { id name }
        }
      }
    `,
    queryName: 'getLecture',
  },
};

export { ENTITY_CONFIG };

// Sub-type labels for lecture-related files
export const LECTURE_SUB_TYPES = {
  'lecture-pdf-de': 'PDF (DE)',
  'lecture-pdf-en': 'PDF (EN)',
  'lecture-presentation': 'Präsentation',
  'lecture-video': 'Video',
};

/**
 * Parse an externalDocumentId into { entityType, id, subType }
 * e.g. "company_abc-123" -> { entityType: "company", id: "abc-123", subType: null }
 *      "lecture-pdf-de_abc-123" -> { entityType: "lecture", id: "abc-123", subType: "lecture-pdf-de" }
 */
export function parseExternalId(externalDocumentId) {
  if (!externalDocumentId) return null;

  // Check long prefixes first (lecture sub-types)
  const subTypePrefixes = Object.keys(LECTURE_SUB_TYPES);
  for (const prefix of subTypePrefixes) {
    if (externalDocumentId.startsWith(prefix + '_')) {
      return {
        entityType: 'lecture',
        id: externalDocumentId.substring(prefix.length + 1),
        subType: prefix,
      };
    }
  }

  // Standard prefix: type_uuid
  const idx = externalDocumentId.indexOf('_');
  if (idx === -1) return null;

  return {
    entityType: externalDocumentId.substring(0, idx),
    id: externalDocumentId.substring(idx + 1),
    subType: null,
  };
}
